import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
// Lot 5.4 — `SetupWizard` et `PlanningApp` ne sont plus montés séparément : le
// Planning annuel est UN module à deux onglets, porté par `PlanningModule`.
import PlanningModule from './components/PlanningModule';
import AdminPanel from './components/AdminPanel';
import AccueilDashboard from './components/AccueilDashboard';
import EdtBuilder from './components/EdtBuilder';
import Enseignants from './components/Enseignants';
import Etudiants from './components/Etudiants';
import Programme from './components/Programme';
import SuiviStageModule from './components/SuiviStageModule';
import PlanningOraux from './components/PlanningOraux';
import StageApercuEnvoi from './components/StageApercuEnvoi';
import StageFicheLiaisonPublique from './components/StageFicheLiaisonPublique';
import VerifierDocument from './components/VerifierDocument';
import ParticipationMaitre from './components/ParticipationMaitre';
import { MotDePasseOublie, MotDePasseLien } from './components/MotDePasse';
import AppSidebar from './components/AppSidebar';
import AppLayout from './components/AppLayout';
import { authAPI, calendrierAPI, departementsAPI } from './services/api';
import { estSuperAdmin, peutAccederAdministration } from './services/droits';

function App() {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(() => {
        const stored = localStorage.getItem('user');
        return stored ? JSON.parse(stored) : null;
    });
    const [config, setConfig] = useState(null);
    const [adminViewDept, setAdminViewDept] = useState(null);
    // Initialiser autoLoading à true dès le départ si on sait qu'on va devoir
    // charger la config automatiquement (non-admin avec un département).
    // Cela évite que les routes redirigent vers /setup pendant le premier render.
    const [autoLoading, setAutoLoading] = useState(() => {
        const storedToken = localStorage.getItem('token');
        const storedUser  = localStorage.getItem('user');
        if (!storedToken || !storedUser) return false;
        try {
            const u = JSON.parse(storedUser);
            // Même règle que l'effet d'auto-chargement : seul le super-administrateur
            // n'a pas de configuration à charger (sa page d'entrée est /admin).
            return u?.is_super_admin !== true && (u?.departements?.length ?? 0) > 0;
        } catch { return false; }
    });
    const [autoLoadError, setAutoLoadError] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    // Liste des années universitaires — partagée sidebar + modules
    const [annees, setAnnees] = useState([]);

    // Année sélectionnée — état unique partagé par TOUS les modules (présents et futurs)
    const [currentAnneeId, setCurrentAnneeId] = useState(null);

    const loadAnnees = () => {
        calendrierAPI.getAllVisible()
            .then(res => {
                const liste = res.data?.annees || [];
                setAnnees(liste);
                const active = liste.find(a => a.active);
                if (active) setCurrentAnneeId(prev => prev || active.id);
            })
            .catch(() => {});
    };

    useEffect(() => {
        if (token && user) loadAnnees();
    }, [token, user]);

    // Rafraîchir le compte et sa carte de droits au démarrage : un changement de
    // profil ou d'exception fait par un administrateur doit être pris en compte
    // sans attendre une reconnexion (la copie de localStorage date de la connexion).
    useEffect(() => {
        if (!token) return;
        authAPI.me()
            .then(res => {
                const compte = res.data?.user;
                if (!compte) return;
                setUser(compte);
                localStorage.setItem('user', JSON.stringify(compte));
            })
            .catch(() => { /* jeton expiré : l'intercepteur d'api.js gère la déconnexion */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const currentSelectedAnnee = annees.find(a => a.id === currentAnneeId);
    const currentAnneeDebut = currentSelectedAnnee
        ? parseInt((currentSelectedAnnee.libelle || '').split('-')[0]) || new Date().getFullYear()
        : new Date().getFullYear();

    // Deux notions distinctes (spec Administration §3), à ne pas confondre :
    //  - `isSuper` : pas de département attaché, sa page d'entrée est /admin ;
    //  - `peutAdministrer` : accès au panneau, super-administrateur OU administrateur
    //    de département. Un administrateur de département reste un utilisateur
    //    normal de SON département : il travaille sur l'Accueil, comme les autres.
    const isSuper = estSuperAdmin(user);
    const peutAdministrer = peutAccederAdministration(user);
    // Lot CO-a : l'administrateur de composante n'est membre d'aucun département (CO1) — comme le
    // super-administrateur, sa page d'entrée est le panneau d'administration.
    const entreeParAdmin = isSuper || (peutAdministrer && (user?.departements?.length ?? 0) === 0);

    // Sauvegarder la route courante (hors /login, /setup) pour restauration après refresh
    useEffect(() => {
        const path = location.pathname;
        if (token && user && path !== '/login' && path !== '/setup') {
            localStorage.setItem('last_route', path);
        }
    }, [location.pathname, token, user]);

    // Auto-chargement pour les non-admins
    useEffect(() => {
        if (token && user && !isSuper && !config) {
            const dept = user?.departements?.[0];
            // restoreRoute=true : après un hard refresh, on retourne à la dernière page visitée
            if (dept) loadConfigForDept(dept, true);
        }
    }, [token, user]);

    // Charge et construit la config pour un département donné
    // restoreRoute : si true, redirige vers la route sauvegardée après succès (pour Ctrl+Shift+R)
    const loadConfigForDept = async (dept, restoreRoute = false) => {
        if (!dept) return false;
        setAutoLoading(true);
        setAutoLoadError(null);
        try {
            const res = await departementsAPI.getPlanningConfig(dept.id);
            const { departement, anneeScolaire, typesJournees, niveaux, sousNiveaux } = res.data;

            const newConfig = {
                anneeScolaireId: anneeScolaire.id,
                departement: {
                    ...dept,
                    titre_planning: departement.titre_planning || '',
                    logo_url: departement.logo_url || null,
                    // Lot 5.3 bis : délai du rappel « enregistrez une version ». `dept`
                    // vient de `user.departements`, qui ne porte que l'identité — les
                    // réglages viennent de la ligne complète renvoyée par le serveur.
                    rappel_sauvegarde_minutes: departement.rappel_sauvegarde_minutes
                },
                sousNiveaux,
                typesJournees: typesJournees.map(t => ({
                    code: t.code,
                    libelle: t.libelle,
                    couleur: t.couleur,
                    couleur_texte: t.couleur_texte || '#FFFFFF',
                    controle: t.controle !== undefined ? t.controle : true,
                    heures_par_jour: t.heures_par_jour !== undefined ? t.heures_par_jour : 7
                })),
                niveaux: niveaux.map(n => {
                    const niveauId = n.niveau_id;
                    const snDuNiveau = sousNiveaux
                        .filter(sn => sn.niveau_id === niveauId)
                        .sort((a, b) => a.ordre - b.ordre);

                    let colonnes;
                    if (snDuNiveau.length === 1) {
                        colonnes = [{
                            label: n.code,
                            code: snDuNiveau[0].code,
                            couleur: snDuNiveau[0].couleur || '#007BFF'
                        }];
                    } else if (snDuNiveau.length > 1) {
                        colonnes = snDuNiveau.map(sn => ({
                            label: n.code + ' ' + (sn.nom.startsWith(n.code) ? sn.nom.substring(n.code.length + 1) : sn.code),
                            code: sn.code,
                            couleur: sn.couleur || '#007BFF'
                        }));
                    } else {
                        colonnes = [{ label: n.code, code: 'FI', couleur: '#007BFF' }];
                    }

                    return { id: niveauId, code: n.code, nom: n.nom, nbGroupes: snDuNiveau.length || 1, colonnes };
                })
            };

            setConfig(newConfig);

            // Restaurer la dernière route visitée (Ctrl+Shift+R)
            if (restoreRoute) {
                const last = localStorage.getItem('last_route');
                const VALID_ROUTES = ['/', '/planning', '/enseignants', '/programme', '/edt'];
                if (last && VALID_ROUTES.includes(last) && last !== '/setup') {
                    navigate(last, { replace: true });
                }
            }

            return true;
        } catch (err) {
            console.error('Erreur auto-chargement config:', err);
            setAutoLoadError('Impossible de charger la configuration automatiquement');
            return false;
        } finally {
            setAutoLoading(false);
        }
    };

    const handleLogin = (newToken, newUser) => {
        setToken(newToken);
        setUser(newUser);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('planningConfig');
        setToken(null);
        setUser(null);
        setConfig(null);
        setAdminViewDept(null);
        setAutoLoadError(null);
        navigate('/login');
    };

    /**
     * Le Paramétrage vient d'enregistrer : la configuration du module change.
     * On la remonte ici — SANS NAVIGUER (lot 5.4).
     *
     * `handleSetupComplete` naviguait vers `/planning` : c'était la fin d'un
     * assistant. `handleBackToSetup`, son symétrique, faisait
     * `localStorage.removeItem('planningConfig')` puis `setConfig(null)` pour aller
     * au Paramétrage — passer d'un onglet à l'autre jetait donc la configuration
     * chargée. Les deux ont disparu avec la fusion des écrans : changer d'onglet
     * n'est plus une navigation.
     */
    const handleConfigChange = (newConfig) => {
        setConfig(newConfig);
    };

    const handleBackToAdmin = () => {
        // Le super-administrateur navigue de département en département : on repart
        // d'une configuration vide pour ne pas garder celle du département quitté.
        // Un administrateur de département n'a qu'un périmètre : on conserve sa
        // configuration, sinon son retour à l'Accueil le renverrait au Paramétrage.
        if (isSuper) {
            localStorage.removeItem('planningConfig');
            setConfig(null);
            setAdminViewDept(null);
        }
        navigate('/admin');
    };

    const handleNavigate = (module) => {
        if (module === 'admin') { handleBackToAdmin(); return; }
        navigate(module === 'accueil' ? '/' : `/${module}`);
    };

    // Navigation vers un autre module quand aucune configuration n'est chargée
    const handleSetupNavigate = async (key) => {
        const dept = adminViewDept || window._lastDept;
        if (dept) {
            const ok = await loadConfigForDept(dept);
            if (ok) { navigate(key === 'accueil' ? '/' : `/${key}`); return; }
        }
        navigate(key === 'accueil' ? '/' : `/${key}`);
    };

    // `cible` : module à ouvrir après le chargement (clé de navigation). Sans cible,
    // on arrive sur l'accueil du département. C'est ce qui permet à l'Administration
    // d'envoyer directement vers un module au lieu de laisser l'utilisateur bloqué
    // faute de configuration chargée.
    const handleViewDepartement = async (dept, cible) => {
        setAdminViewDept(dept);
        localStorage.removeItem('planningConfig');
        setConfig(null);
        const ok = await loadConfigForDept(dept);
        const route = cible && cible !== 'accueil' ? `/${cible}` : '/';
        navigate(ok ? route : '/setup');
    };

    // ── Bandeau année non active ─────────────────────────────────────────────
    // Déclaré AVANT tout return (règle des hooks React)
    // currentAnneeId est le seul état d'année — garanti correct sur tous les modules
    const BANNER_H = 36;
    const activeYear   = annees.find(a => a.active);
    const selectedYear = annees.find(a => a.id === currentAnneeId);
    const showBanner   = !!(token && user
        && activeYear && selectedYear
        && activeYear.id !== selectedYear.id
        && !location.pathname.startsWith('/login')
        && !location.pathname.startsWith('/admin'));

    useEffect(() => {
        document.body.style.paddingTop = showBanner ? `${BANNER_H}px` : '';
        return () => { document.body.style.paddingTop = ''; };
    }, [showBanner]);

    // ⚠️ Lot 5.1 — `handleActiverAnneeGlobal` a été retiré. Rendre une année active
    // engage TOUS les départements : c'est un acte de gouvernance globale, réservé
    // au super-administrateur depuis 0.B.5 et livré dans
    // Administration → Calendriers au lot 3 du Bloc 0. Le bouton du bandeau
    // répondait 403 à tout le monde sauf au super-administrateur, sans expliquer
    // pourquoi. Le bandeau garde son INFORMATION (année consultée / année active)
    // et propose un lien vers l'écran qui sait le faire, uniquement à qui peut y
    // accéder.

    /**
     * ⚠️ LES PAGES PUBLIQUES PASSENT AVANT LA PORTE DE CONNEXION — et c'est la
     * condition pour qu'elles existent.
     *
     * Le garde-fou ci-dessous renvoie TOUTE adresse vers `Login` quand personne
     * n'est connecté. Or ces deux pages s'adressent à des étudiants et, au lot
     * 3.2 bis, à des maîtres de stage : ils n'ont pas de compte et n'en auront
     * pas. Déclarées après, elles afficheraient une page de connexion à
     * quelqu'un qui n'a rien à y saisir — et le lien du courriel serait mort.
     *
     * Elles sont donc rendues AVANT, et **quel que soit l'état de session** :
     * un enseignant déjà connecté qui clique le lien depuis sa boîte mail doit
     * voir le formulaire, pas le tableau de bord.
     *
     * ⚠️ Sans `AppLayout` : ni sidebar, ni topbar, ni sélecteur d'année. Ce sont
     * des pages, pas des écrans de l'application.
     */
    const routesPubliques = [
        // Le lien COMMUN, envoyé à toute la promotion (écart E24).
        <Route key="pub-commune" path="/stage/fiche-liaison/:code"
               element={<StageFicheLiaisonPublique porte="commune" />} />,
        // Le lien TOKENISÉ des relances ciblées.
        <Route key="pub-token" path="/stage/etudiant/:token"
               element={<StageFicheLiaisonPublique porte="tokenisee" />} />,
        /**
         * Le lien du MAÎTRE DE STAGE — étape ④ (lot 3.2 bis).
         *
         * ⚠️ C'est LUI le destinataire le plus extérieur de tout le module : il
         * n'appartient pas à l'établissement, n'aura jamais de compte, et ouvre
         * ce lien depuis sa boîte professionnelle. Si cette route se retrouvait
         * un jour derrière la porte de connexion, la chaîne s'arrêterait là —
         * sans que l'application le sache, puisque personne ne le lui dirait.
         */
        <Route key="pub-maitre" path="/stage/fiche/:token"
               element={<StageFicheLiaisonPublique porte="maitre" />} />,
        /**
         * ⚠️ LES ADRESSES COURTES — celles qu'on envoie désormais (écart E66).
         *
         * Le lien s'affiche EN ENTIER dans le message : un corps de `mailto:`
         * est du texte brut, impossible d'y poser un lien sur un libellé. Chaque
         * caractère du chemin est donc un caractère de plus sous les yeux du
         * destinataire — 83 avant, 49 maintenant.
         *
         * ⚠️ `/s/` ne dit PAS quel formulaire ouvrir : c'est **le jeton** qui le
         * dit, et le serveur qui tranche. Une seule adresse pour l'étudiant, le
         * maître de stage, et les trois jetons à venir des lots 3.4 et 3.6.
         *
         * ⚠️ Les quatre adresses longues restent déclarées au-dessus : des
         * courriels déjà partis les portent, et un lien mort chez une entreprise
         * ne se rattrape pas.
         */
        <Route key="pub-court" path="/s/:token"
               element={<StageFicheLiaisonPublique porte="auto" />} />,
        // PO.4b-2 (D28) : « Vérifier ce document » — le QR des convocations ; publique, aucune donnée personnelle
        <Route key="pub-verifier" path="/verifier/:jeton" element={<VerifierDocument />} />,
        <Route key="pub-participation" path="/participation/:jeton" element={<ParticipationMaitre />} />,
        // Lot INV (v83) : l'accès aux comptes — « mot de passe oublié », et le lien d'invitation / de réinitialisation
        <Route key="pub-mdp-oublie" path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />,
        <Route key="pub-mdp-lien" path="/mot-de-passe/:jeton" element={<MotDePasseLien />} />,
        <Route key="pub-campagne" path="/c/:code"
               element={<StageFicheLiaisonPublique porte="commune" />} />
    ];

    // Non connecté — toutes les autres routes redirigent vers /login
    if (!token || !user) {
        return (
            <Routes>
                {routesPubliques}
                <Route path="*" element={<Login onLogin={handleLogin} />} />
            </Routes>
        );
    }

    // Chargement auto en cours
    if (autoLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <p>Chargement du planning...</p>
            </div>
        );
    }

    // Sidebar partagée pour les modules sans AppLayout (AccueilDashboard admin, etc.)
    const sidebar = (activeKey) => (
        <AppSidebar
            activeKey={activeKey}
            user={user}
            config={config}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            annees={annees}
            anneeId={currentAnneeId}
            onAnneeChange={setCurrentAnneeId}
        />
    );

    // Props sidebar communs — passés à tous les AppLayout (présents et futurs)
    const sharedLayoutProps = {
        user,
        config,
        annees,
        anneeId: currentAnneeId,
        onAnneeChange: setCurrentAnneeId,
        onLogout: handleLogout,
    };

    return (
        <>
        {showBanner && (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
                height: BANNER_H, background: '#b45309',
                color: '#fff', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 16, fontSize: 13, fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
            }}>
                <span>⚠️ Année non active — vous consultez <strong>{selectedYear?.libelle}</strong> · Année active : <strong>{activeYear?.libelle}</strong></span>
                {peutAdministrer && (
                    <button
                        onClick={() => navigate('/admin')}
                        title="Changer l'année active engage tous les départements : cela se fait dans Administration → Calendriers"
                        style={{
                            background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.5)',
                            color: '#fff', borderRadius: 4, padding: '2px 10px',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer'
                        }}
                    >
                        Gérer les années
                    </button>
                )}
            </div>
        )}
        <Routes>
            {/* Login — redirige vers accueil si déjà connecté */}
            <Route path="/login" element={<Navigate to="/" replace />} />

            {/* Administration — admin uniquement */}
            <Route path="/admin" element={
                peutAdministrer
                    ? <AdminPanel user={user} onLogout={handleLogout} onViewDepartement={handleViewDepartement} onNavigate={handleNavigate} />
                    : <Navigate to="/" replace />
            } />

            {/* `/setup` n'est plus un écran : c'est l'onglet Paramétrage du Planning.
                La route SURVIT en redirection — six endroits de ce fichier y envoyaient
                encore, et rien ne se retire avant d'avoir son remplacement. */}
            <Route path="/setup" element={<Navigate to="/planning?onglet=parametrage" replace />} />

            {/* EDT Builder */}
            <Route path="/edt" element={
                config
                    ? <EdtBuilder user={user} config={config} onNavigate={handleNavigate} onLogout={handleLogout} />
                    : <Navigate to={isSuper ? '/' : '/setup'} replace />
            } />

            {/* Planning annuel — les deux onglets, une seule route.
                Plus de redirection vers `/setup` quand la configuration manque : c'est
                le Paramétrage qui sert à la créer, et `/setup` redirigeant ici, les
                deux se seraient renvoyé la balle indéfiniment. `PlanningModule` ouvre
                l'onglet Paramétrage et grise le Calendrier, avec son motif.
                `onNavigate` : sans configuration, quitter le module vers un autre
                demande de la charger d'abord — c'est ce que faisait `/setup`. */}
            <Route path="/planning" element={
                <AppLayout
                    {...sharedLayoutProps}
                    activeKey="planning"
                    onNavigate={config ? handleNavigate : handleSetupNavigate}
                >
                    {!config && autoLoadError && (
                        <div style={{ maxWidth: 600, margin: '20px auto' }}>
                            <div className="alert alert-error">{autoLoadError}</div>
                        </div>
                    )}
                    <PlanningModule
                        token={token}
                        user={user}
                        config={config}
                        onConfigChange={handleConfigChange}
                        onLogout={handleLogout}
                        initialDept={adminViewDept || window._lastDept}
                        anneeDebut={currentAnneeDebut}
                        anneeId={currentAnneeId || config?.anneeScolaireId}
                        onAnneeChange={setCurrentAnneeId}
                    />
                </AppLayout>
            } />

            {/* Accueil — page par défaut */}
            <Route path="/" element={
                !config
                    ? entreeParAdmin
                        /* Super-administrateur sans département chargé : sa page d'entrée
                           est le panneau d'administration, pas un écran vide. Un
                           administrateur de département, lui, a son département : sa
                           configuration se charge et il arrive sur l'Accueil. */
                        ? <Navigate to="/admin" replace />
                        : <Navigate to="/setup" replace />
                    : <AccueilDashboard
                        user={user}
                        config={config}
                        onNavigate={handleNavigate}
                        onLogout={handleLogout}
                      />
            } />

            {/* Enseignants */}
            <Route path="/enseignants" element={
                !config
                    ? <Navigate to={isSuper ? '/' : '/setup'} replace />
                    : <AppLayout
                        {...sharedLayoutProps}
                        activeKey="enseignants"
                        onNavigate={handleNavigate}
                    >
                        <Enseignants config={config} anneeId={currentAnneeId} user={user} />
                    </AppLayout>
            } />

            {/* Étudiants */}
            <Route path="/etudiants" element={
                !config
                    ? <Navigate to={isSuper ? '/' : '/setup'} replace />
                    : <AppLayout
                        {...sharedLayoutProps}
                        activeKey="etudiants"
                        onNavigate={handleNavigate}
                    >
                        <Etudiants config={config} user={user} anneeId={currentAnneeId} annees={annees} />
                    </AppLayout>
            } />

            {/* ⚠️ Les pages publiques AUSSI pour un utilisateur connecté : un
                enseignant qui clique le lien depuis sa boîte mail doit voir le
                formulaire, pas le tableau de bord. */}
            {routesPubliques}

            {/* Suivi stages / alternance — lot 3.0 : la coquille et le paramétrage */}
            <Route path="/stage" element={
                !config
                    ? <Navigate to={isSuper ? '/' : '/setup'} replace />
                    : <AppLayout
                        {...sharedLayoutProps}
                        activeKey="stage"
                        onNavigate={handleNavigate}
                    >
                        <SuiviStageModule config={config} user={user} anneeId={currentAnneeId} annees={annees} />
                    </AppLayout>
            } />

            {/* Planning oraux — lot PO.0 (v77). Adresse construite depuis la clé du module (D5). */}
            <Route path="/soutenance" element={
                !config
                    ? <Navigate to={isSuper ? '/' : '/setup'} replace />
                    : <AppLayout {...sharedLayoutProps} activeKey="soutenance" onNavigate={handleNavigate}>
                        <PlanningOraux config={config} user={user} anneeId={currentAnneeId} annees={annees} />
                    </AppLayout>
            } />

            {/* Lot 3.S-b2 (SM8) : l'aperçu vers lequel pointe le lien d'un message d'ESSAI — connexion requise. */}
            <Route path="/stage/apercu/:type/:configId" element={
                !config
                    ? <Navigate to={isSuper ? '/' : '/setup'} replace />
                    : <AppLayout {...sharedLayoutProps} activeKey="stage" onNavigate={handleNavigate}>
                        <StageApercuEnvoi config={config} user={user} />
                    </AppLayout>
            } />

            {/* Programme de formation */}
            <Route path="/programme" element={
                !config
                    ? <Navigate to={isSuper ? '/' : '/setup'} replace />
                    : <AppLayout
                        {...sharedLayoutProps}
                        activeKey="programme"
                        onNavigate={handleNavigate}
                    >
                        <Programme config={config} anneeId={currentAnneeId} annees={annees} />
                    </AppLayout>
            } />

            {/* Toute URL inconnue → accueil */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </>
    );
}

export default App;
